import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Card,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CustomFilterV2, TableV2 } from '../../../components/v2';
import { tableV2LoadingLinearSx } from '../../../components/v2/tableV2Styles';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import * as actionsCalendarioFeriado from '../../../store/calendarioFeriado/actions';
import * as selectorsCalendarioFeriado from '../../../store/calendarioFeriado/reducer';
import CalendarGrid from '../Calendario/CalendarGrid';
import {
  buildMonthDays,
  groupFeriadosByDate,
  TIPO_COLOR,
  TIPO_LABEL as TIPO_LABEL_GRID,
} from '../Calendario/dateUtils';
import Formulario from './Formulario';
import InvalidarDialog from './InvalidarDialog';

moment.locale('pt-br');

const TIPO_LABEL = {
  NACIONAL: 'Nacional',
  ESTADUAL: 'Estadual',
  MUNICIPAL: 'Municipal',
};

const STATUS_LABEL = {
  VALIDO: 'Válido',
  INVALIDO: 'Inválido',
};

const TIPO_OPTIONS = [
  { value: 'NACIONAL', label: 'Nacional' },
  { value: 'ESTADUAL', label: 'Estadual' },
  { value: 'MUNICIPAL', label: 'Municipal' },
];

const STATUS_OPTIONS = [
  { value: 'VALIDO', label: 'Válido' },
  { value: 'INVALIDO', label: 'Inválido' },
];

const ORIGEM_LABEL = {
  AUTOMATICO: 'Automática',
  MANUAL: 'Manual',
};

const ORIGEM_OPTIONS = [
  { value: 'AUTOMATICO', label: 'Automática' },
  { value: 'MANUAL', label: 'Manual' },
];

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map((uf) => ({ value: uf, label: uf }));

const buildDefaultFilter = () => ({
  ano: moment().year(),
  uf: 'MG',
  codigoIbge: null,
  municipioNome: null,
  tipo: 'MUNICIPAL',
  origens: null,
  statuses: null,
});

/** Normaliza multiSelect: array vazio / null → null (sem filtro). */
const toMultiFilterValue = (value) => {
  if (value == null) return null;
  const list = Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
  return list.length ? list : null;
};

const toApiFilter = (filter) => ({
  ano: filter?.ano ?? null,
  uf: filter?.uf || null,
  codigoIbge: filter?.codigoIbge || null,
  tipo: filter?.tipo || null,
  origens: toMultiFilterValue(filter?.origens),
  statuses: toMultiFilterValue(filter?.statuses),
});

const formatMultiChipLabel = (values, labelMap) => {
  const list = toMultiFilterValue(values);
  if (!list) return 'todos';
  return list.map((v) => labelMap[v] || v).join(', ');
};

const viewModeToggleSx = {
  height: 36.5,
  mr: 2,
  alignSelf: 'center',
  '& .MuiToggleButtonGroup-grouped': {
    borderColor: 'divider',
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderLeft: '1px solid',
      borderLeftColor: 'divider',
    },
    '&.Mui-selected + .MuiToggleButtonGroup-grouped': {
      borderLeftColor: 'primary.main',
    },
  },
  '& .MuiToggleButton-root': {
    px: 1.25,
    py: 0,
    height: 36.5,
    gap: 0.5,
    minWidth: 'auto',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textTransform: 'none',
    color: 'text.secondary',
    border: '1px solid',
    borderColor: 'divider',
    '&.Mui-selected': {
      zIndex: 1,
      color: 'primary.main',
      bgcolor: 'action.selected',
      border: '1px solid',
      borderColor: 'primary.main',
      '&:hover': {
        bgcolor: 'action.selected',
      },
    },
  },
};

const ViewModeToggle = ({ value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    onChange={onChange}
    aria-label="Modo de visualização"
    sx={viewModeToggleSx}
  >
    <ToggleButton value="lista" aria-label="Lista">
      <ViewListOutlinedIcon sx={{ fontSize: 16 }} />
      Lista
    </ToggleButton>
    <ToggleButton value="calendario" aria-label="Calendário">
      <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
      Calendário
    </ToggleButton>
  </ToggleButtonGroup>
);

ViewModeToggle.propTypes = {
  value: PropTypes.oneOf(['lista', 'calendario']).isRequired,
  onChange: PropTypes.func.isRequired,
};

const CalendarioAdmin = ({
  feriadosAdmin,
  loadingCalendarioAdmin,
  municipiosUsuariosAtivos,
  invalidarFeriado,
  revalidarFeriado,
  loadCalendarioAdminCalendar,
  loadMunicipiosUsuariosAtivos,
  cleanCalendarioFeriado,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openInvalidar, setOpenInvalidar] = useState(false);
  const [invalidarRow, setInvalidarRow] = useState(null);
  const [reload, setReload] = useState(false);
  const [viewMode, setViewMode] = useState('lista');
  const [referenceDate, setReferenceDate] = useState(moment());
  const [defaultFilter] = useState(buildDefaultFilter);
  const [localFilter, setLocalFilter] = useState(defaultFilter);

  const apiFilter = useMemo(() => toApiFilter(localFilter), [localFilter]);

  const municipioOptions = useMemo(() => {
    const list = (municipiosUsuariosAtivos || []).filter((m) => m?.codigoIbge);
    const scoped = localFilter.uf
      ? list.filter((m) => !m.uf || m.uf === localFilter.uf)
      : list;
    return scoped.map((m) => ({
      value: String(m.codigoIbge),
      label: m.municipioNome && m.uf
        ? `${m.municipioNome} / ${m.uf}`
        : (m.municipioNome || String(m.codigoIbge)),
    }));
  }, [municipiosUsuariosAtivos, localFilter.uf]);

  // Baseado na lista bruta do servidor (não no scoped por UF): UF sem municípios
  // curados mostra select vazio, sem cair no catálogo nacional (~5.570).
  const useCuratedMunicipios = (municipiosUsuariosAtivos || []).some((m) => m?.codigoIbge);

  const municipioChipLabel = useMemo(() => {
    if (!localFilter.codigoIbge) return 'todos';
    if (localFilter.municipioNome) {
      return localFilter.uf
        ? `${localFilter.municipioNome} / ${localFilter.uf}`
        : localFilter.municipioNome;
    }
    const match = municipioOptions.find((o) => o.value === String(localFilter.codigoIbge));
    return match?.label || localFilter.codigoIbge;
  }, [localFilter, municipioOptions]);

  useEffect(() => {
    loadMunicipiosUsuariosAtivos();
    return () => {
      cleanCalendarioFeriado();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewMode !== 'calendario') return;
    loadCalendarioAdminCalendar(apiFilter);
  }, [viewMode, apiFilter, reload, loadCalendarioAdminCalendar]);

  useEffect(() => {
    const anoFiltro = localFilter.ano || moment().year();
    setReferenceDate((prev) => {
      if (prev.year() === anoFiltro) return prev;
      return moment(prev).year(anoFiltro);
    });
  }, [localFilter.ano]);

  const feriadosByDate = useMemo(() => groupFeriadosByDate(feriadosAdmin), [feriadosAdmin]);

  const days = useMemo(() => buildMonthDays(referenceDate), [referenceDate]);

  const periodoLabel = useMemo(() => referenceDate.format('MMMM YYYY'), [referenceDate]);

  const navigateMonth = useCallback((delta) => {
    setReferenceDate((prev) => {
      const next = moment(prev).add(delta, 'month');
      setLocalFilter((filter) => {
        if (filter.ano === next.year()) return filter;
        return { ...filter, ano: next.year() };
      });
      return next;
    });
  }, []);

  const goToToday = useCallback(() => {
    const today = moment();
    setReferenceDate(today);
    setLocalFilter((filter) => {
      if (filter.ano === today.year()) return filter;
      return { ...filter, ano: today.year() };
    });
  }, []);

  const onAdd = () => {
    setSelectedRow(null);
    setOpen(true);
  };

  const onEdit = (rowId, row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const onClose = (isSave) => {
    setOpen(false);
    setSelectedRow(null);
    if (isSave) setReload((r) => !r);
  };

  const handleViewModeChange = (_event, next) => {
    if (next) setViewMode(next);
  };

  const rowActions = [
    {
      key: 'invalidar',
      icon: <NotInterestedIcon fontSize="small" sx={{ color: 'error.main' }} />,
      title: 'Invalidar',
      show: (row) => row.status === 'VALIDO',
      onClick: (rowId, row) => {
        setInvalidarRow(row);
        setOpenInvalidar(true);
      },
    },
    {
      key: 'revalidar',
      icon: <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />,
      title: 'Revalidar',
      show: (row) => row.status === 'INVALIDO',
      onClick: (rowId) => {
        revalidarFeriado(rowId, (ok) => {
          if (ok) setReload((r) => !r);
        });
      },
    },
  ];

  const filterFields = [
    { field: 'useAno', md: 4 },
    { field: 'useSearchSelect', key: 'tipo', label: 'Tipo', md: 4, options: TIPO_OPTIONS },
    { field: 'useSearchSelect', key: 'uf', label: 'UF', md: 4, options: UF_OPTIONS },
    useCuratedMunicipios
      ? {
        field: 'useSearchSelect',
        key: 'codigoIbge',
        label: 'Município',
        md: 12,
        options: municipioOptions,
      }
      : {
        field: 'useMunicipio',
        md: 12,
        multiSelect: false,
        key: 'codigoIbge',
        nomeKey: 'municipioNome',
      },
    { field: 'useSearchSelect', key: 'origens', label: 'Origem', md: 6, options: ORIGEM_OPTIONS, multiSelect: true },
    { field: 'useSearchSelect', key: 'statuses', label: 'Status', md: 6, options: STATUS_OPTIONS, multiSelect: true },
  ];

  const calendarLoadingSx = {
    opacity: loadingCalendarioAdmin ? 0.38 : 1,
    transition: (theme) =>
      theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shorter,
      }),
    pointerEvents: loadingCalendarioAdmin ? 'none' : 'auto',
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Calendário Administrativo e Gestão de Feriados</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Ano: ${localFilter.ano ?? 'todos'}`} onClick={() => setOpenFilter(true)} />
          <Chip size="small" label={`UF: ${localFilter.uf || 'todas'}`} onClick={() => setOpenFilter(true)} />
          <Chip size="small" label={`Município: ${municipioChipLabel}`} onClick={() => setOpenFilter(true)} />
          <Chip
            size="small"
            label={`Tipo: ${localFilter.tipo ? TIPO_LABEL[localFilter.tipo] : 'todos'}`}
            onClick={() => setOpenFilter(true)}
          />
          <Chip
            size="small"
            label={`Origem: ${formatMultiChipLabel(localFilter.origens, ORIGEM_LABEL)}`}
            onClick={() => setOpenFilter(true)}
          />
          <Chip
            size="small"
            label={`Status: ${formatMultiChipLabel(localFilter.statuses, STATUS_LABEL)}`}
            onClick={() => setOpenFilter(true)}
          />
        </Stack>

        {viewMode === 'lista' ? (
          <TableV2
            useApi
            fetchUrl="calendario/filter"
            headerActions={[
              {
                key: 'view-mode',
                node: (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 36.5 }}>
                    <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
                  </Box>
                ),
              },
              { label: 'Novo', onClick: onAdd, startIcon: <AddIcon />, variant: 'contained' },
            ]}
            actions={rowActions}
            columns={[
              { field: 'dataFormatted', headerName: 'Data', width: 120 },
              { field: 'nome', headerName: 'Nome', flex: 1, minWidth: 180 },
              {
                field: 'tipo',
                headerName: 'Tipo',
                width: 130,
                renderCell: ({ row }) => TIPO_LABEL[row.tipo] || row.tipo,
              },
              {
                field: 'municipioUf',
                headerName: 'Município/UF',
                flex: 0.8,
                minWidth: 160,
                renderCell: ({ row }) =>
                  row.codigoIbge ? `${row.municipioNome || ''} / ${row.uf || ''}` : (row.uf || '—'),
              },
              {
                field: 'status',
                headerName: 'Status',
                width: 140,
                renderCell: ({ row }) =>
                  renderStatus({
                    value: row.status,
                    label: row.status === 'VALIDO' ? 'Válido' : 'Inválido',
                  }),
              },
              {
                field: 'origem',
                headerName: 'Origem',
                width: 130,
              },
              {
                field: 'atualizadoEm',
                headerName: 'Última alteração',
                flex: 0.8,
                minWidth: 200,
                renderCell: ({ row }) =>
                  row.atualizadoEm
                    ? `${moment(row.atualizadoEm).format('DD/MM/YYYY')} — ${row.usuarioNome || '—'}`
                    : '—',
              },
            ]}
            onEdit={onEdit}
            filterData={apiFilter}
            refreshKey={reload}
          />
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {loadingCalendarioAdmin && (
              <LinearProgress
                sx={{
                  ...tableV2LoadingLinearSx,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                }}
              />
            )}
            <Stack spacing={2} sx={{ p: 2, ...calendarLoadingSx }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton size="small" onClick={() => navigateMonth(-1)} aria-label="Mês anterior">
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: 160, textAlign: 'center', textTransform: 'capitalize', fontWeight: 600 }}
                  >
                    {periodoLabel}
                  </Typography>
                  <IconButton size="small" onClick={() => navigateMonth(1)} aria-label="Próximo mês">
                    <ChevronRightIcon />
                  </IconButton>
                  <Button size="small" onClick={goToToday}>
                    Hoje
                  </Button>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0}>
                  <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAdd}
                  >
                    Novo
                  </Button>
                </Stack>
              </Stack>

              <CalendarGrid
                days={days}
                referenceDate={referenceDate}
                feriadosByDate={feriadosByDate}
                viewMode="month"
                showLocalidade={!localFilter.codigoIbge}
              />

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                {Object.keys(TIPO_LABEL_GRID).map((tipo) => (
                  <Stack key={tipo} direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: TIPO_COLOR[tipo]?.main,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {TIPO_LABEL_GRID[tipo]}
                    </Typography>
                  </Stack>
                ))}
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: '1.5px dashed',
                      borderColor: 'error.main',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Cancelado
                  </Typography>
                </Stack>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Visualização apenas — para editar ou invalidar, use o modo Lista.
              </Typography>
            </Stack>
          </Card>
        )}

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          fields={filterFields}
          onFilter={(filterData) => {
            const next = {
              ...defaultFilter,
              ...filterData,
              ano: filterData.ano ?? defaultFilter.ano,
              origens: toMultiFilterValue(filterData.origens),
              statuses: toMultiFilterValue(filterData.statuses),
            };
            if (useCuratedMunicipios && next.codigoIbge) {
              const match = municipioOptions.find((o) => o.value === String(next.codigoIbge));
              if (match?.label) {
                const [nome] = match.label.split(' / ');
                next.municipioNome = nome || null;
              }
            }
            if (!next.codigoIbge) {
              next.municipioNome = null;
            }
            setLocalFilter(next);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={selectedRow?.id || null}
          row={selectedRow}
          open={open}
          onClose={onClose}
        />

        <InvalidarDialog
          open={openInvalidar}
          feriado={invalidarRow}
          onClose={() => { setOpenInvalidar(false); setInvalidarRow(null); }}
          onConfirm={(motivo) => {
            invalidarFeriado(invalidarRow.id, motivo, (ok) => {
              if (ok) setReload((r) => !r);
            });
            setOpenInvalidar(false);
            setInvalidarRow(null);
          }}
        />
      </Stack>
    </Box>
  );
};

CalendarioAdmin.propTypes = {
  feriadosAdmin: PropTypes.array,
  loadingCalendarioAdmin: PropTypes.bool,
  municipiosUsuariosAtivos: PropTypes.array,
  invalidarFeriado: PropTypes.func.isRequired,
  revalidarFeriado: PropTypes.func.isRequired,
  loadCalendarioAdminCalendar: PropTypes.func.isRequired,
  loadMunicipiosUsuariosAtivos: PropTypes.func.isRequired,
  cleanCalendarioFeriado: PropTypes.func.isRequired,
};

CalendarioAdmin.defaultProps = {
  feriadosAdmin: [],
  loadingCalendarioAdmin: false,
  municipiosUsuariosAtivos: [],
};

const mapStateToProps = (state) => ({
  feriadosAdmin: selectorsCalendarioFeriado.getFeriadosAdmin(state),
  loadingCalendarioAdmin: selectorsCalendarioFeriado.getLoadingCalendarioAdmin(state),
  municipiosUsuariosAtivos: selectorsCalendarioFeriado.getMunicipiosUsuariosAtivos(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actionsCalendarioFeriado,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CalendarioAdmin));
